import { Alert, Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect } from "react";
import { useClickwrapTerms } from "../../modules/clickwrap/use-clickwrap-terms";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { useFlags } from "../../server/optimizely";
import NotFound from "../not-found";

const openLinksInNewTab = (html: string): string => {
  const document = new DOMParser().parseFromString(html, "text/html");

  document.querySelectorAll("a").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  return document.body.innerHTML;
};

const AiPage: FC = () => {
  const {
    flags: { ai_pii_clickwrap: aiPiiClickwrap },
    flagsLoaded,
  } = useFlags("ai_pii_clickwrap");

  const clickwrapTerms = useClickwrapTerms("ai_pii");
  const { status, fetchClickwrapTerms } = clickwrapTerms;

  useEffect(() => {
    if (status !== "idle") return;
    void fetchClickwrapTerms();
  }, [status, fetchClickwrapTerms]);

  if (!flagsLoaded) return <LinearProgress />;
  if (!aiPiiClickwrap) return <NotFound />;

  return (
    <Page title="AI Assistant | ChangeEngine">
      <PageHeader>
        <PageHeading
          heading="AI Assistant"
          subheading="Control what employee data the AI assistant is allowed to show you"
        />
      </PageHeader>
      <PageContent>
        {clickwrapTerms.status === "loading" || clickwrapTerms.status === "idle" ? (
          <LinearProgress />
        ) : null}

        {clickwrapTerms.status === "error" ? (
          <Alert severity="error">
            {clickwrapTerms.errorMessage ?? "Couldn’t load the acknowledgement. Please retry."}
          </Alert>
        ) : null}

        {clickwrapTerms.status === "loaded" && clickwrapTerms.hasAcceptedCurrentVersion ? (
          <Alert severity="success" data-testid="ai-pii-accepted">
            Accepted. The AI assistant can show individual employee details, such as who a
            communication failed to reach.
          </Alert>
        ) : null}

        {clickwrapTerms.status === "loaded" && !clickwrapTerms.hasAcceptedCurrentVersion ? (
          <Stack spacing={2} data-testid="ai-pii-terms">
            <Typography variant="body2">
              Until this is accepted, the AI assistant reports delivery totals only, and never names
              individual employees.
            </Typography>
            <Box
              sx={{
                maxHeight: "60vh",
                overflowY: "auto",
                "& h1, & h2, & h3, & h4": { mt: 0 },
                "& p, & li": { whiteSpace: "pre-wrap" },
              }}
              data-content-format={clickwrapTerms.contentFormat}
              dangerouslySetInnerHTML={{ __html: openLinksInNewTab(clickwrapTerms.contentHtml) }}
            />
            <Box>
              <Button
                variant="contained"
                data-analytics-id="ai-pii-clickwrap-accept"
                onClick={() => void clickwrapTerms.acceptClickwrapTerms()}
              >
                Accept
              </Button>
            </Box>
          </Stack>
        ) : null}
      </PageContent>
    </Page>
  );
};

export default AiPage;
